import {useMantineTheme} from '@mantine/core';
import {ElkEdgeSection, ElkLabel} from 'elkjs';
import React from 'react';
import {BaseEdge, getBezierPath} from 'reactflow';

import {assert} from 'Asserts';
import {midPoint, Point, pointToString} from 'util/geometry/GeometryConstants';

import {DEFAULT_EDGE_OPTIONS, EdgeType, ElkSplineEdgeProps, ElkSplineEdgeRendererData} from './edgeOptions';
import {getEdgePathStyle} from './edgeUtils';

const DEFAULT_ELK_SPLINE_EDGE_OPTIONS: ElkSplineEdgeRendererData = {
  ...DEFAULT_EDGE_OPTIONS,
  type: EdgeType.ELK_SPLINE,
  elkEdgeData: {
    sections: [],
    labels: [],
  },
};

// How much a node needs to move before its considered to be "dragged" and ELK edges
// are considered invalid.
export const DRAG_BUFFER_PIXELS = 15;

/**
 * Generates a Bezier spline from a list of control points produced by the ELK layout algorithm.
 *
 * When using ELK's SPLINES option for edge routing, curves are produced. The ELK documentation states these
 * curves are specified as a set of control points for a piecewise cubic spline. A piecewise cubic spline is a
 * curve composed of segments, where each segment is defined by a cubic polynomial. The control points define
 * the segments.
 *
 * Unfortunately there's some disagreement online re: how to interpret the control points produced by ELK.
 * We'd expect num(control points + end point) to be divisible by 3, but that's not always the case. The code
 * below is adapted from a github issue inquiring about this. The author of that post tried to get an answer
 * on how to interpret the control points from the ELK authors, but didn't receive one. The author then
 * developed these theories regarding what ELK is returning, based on what other systems often return when
 * specifying curves.
 *  1. In some cases, ELK returns the control points for a series of quadratic bezier curves. This is identified
 *     by the number of control points being a multiple of 2.
 * 2. In some cases, ELK returns the control points for a series of fully-specified cubic bezier curves. This
 *    is identified by the number of control points being a multiple of 3.
 * 3. In some cases, ELK returns the control points for a series of cubic bezier curves, but omits the midpoints
 *     between neighboring segments. This is apparently a common practice when specifying a series of cubic
 *     bezier curves. This case is identified by the number of control points not being a multiple of 3 or 2.
 *     In this case, the missing midpoints need to be re-computed before drawing the curve.
 *
 * The code below adapts this solution. While it is based on uncomfirmed speculation re: what ELK is returning
 * for SPLINES, it seems to work well in practice. See these links for more information:
 *
 * ELK documentation: https://eclipse.dev/elk/reference/options/org-eclipse-elk-edgeRouting.html
 * Github issue this code is adapted from: https://github.com/eclipse-elk/elk/issues/848
 * Stackoverflow issue about adding missing control points: https://stackoverflow.com/a/72577667/1010492
 *
 * @param points - The set of control points to use, with the start and end points included.
 * @returns The SVG path string for the bezier spline.
 * @package
 */
export function getBezierSplinePathFromElkPoints(points: Array<Point>): string {
  assert(points.length >= 2, 'Expected at least two points to compute spline path.');
  const [start, ...controlPoints] = points;

  // Start the SVG path by "moving" to the start point.
  const path = [`M ${pointToString(start)}`];

  if (controlPoints.length === 1) {
    // There's only one control point (the end point), so we draw a straight line.
    path.push(`L ${pointToString(controlPoints[0])}`);
  } else if (controlPoints.length % 3 === 0) {
    // The remaining control points are a multiple of 3, so we can form the spline as a series of cubic bezier curves.
    for (let i = 0; i < controlPoints.length; i += 3) {
      const [ctrlPt1, ctrlPt2, segmentEnd] = controlPoints.slice(i, i + 3);
      path.push(`C ${pointToString(ctrlPt1)}, ${pointToString(ctrlPt2)}, ${pointToString(segmentEnd)}`);
    }
  } else if (controlPoints.length % 2 === 0) {
    // The remaining control points are a multiple of 2, so we can form the spline as a series of quadratic bezier curves.
    for (let i = 0; i < controlPoints.length; i += 2) {
      const [ctrlPt, segmentEnd] = controlPoints.slice(i, i + 2);
      path.push(`Q ${pointToString(ctrlPt)}, ${pointToString(segmentEnd)}`);
    }
  } else {
    // The control points are not a multiple of 3 or 2. We take this to mean that "midpoint" control points
    // were omitted by ELK, so we interpolate those missing midpoints. It's not uncommon for systems specifying
    // a series of cubic bezier curves to omit these midpoints as control points in a pattern matching how we
    // reconstruct them below. See the stackoverflow issue linked above.
    //
    // To infer the missing midpoints, we:
    // 1. Take the last 3 original control points as a triplet of control points and leave untouched.
    // 2. Divide the remaining control points into pairs.
    // 3. For each pair, add a missing control point to form a triplet.
    //    - For pairs p1 and p2 appearing consecutively in the array, the control point we add to p1
    //      is the midpoint between the last point in p1 and the first point in p2.
    // This results in a set of control points that divides into triplets that define cubic bezier
    // curve segments. Since we have a set of triplets after this loop, the recursive call below should only
    // have a depth of 1.
    for (let i = controlPoints.length - 3; i >= 2; i -= 2) {
      const missingPoint = midPoint(controlPoints[i - 1], controlPoints[i]);
      controlPoints.splice(i, 0, missingPoint);
    }
    return getBezierSplinePathFromElkPoints([start, ...controlPoints]);
  }

  return path.join(' ');
}

/**
 * Check if the ELK edge start and end match the ReactFlow edge start and end.
 *
 * @param elkStart - The start point of the ELK edge.
 * @param elkEnd - The end point of the ELK edge.
 * @param reactFlowStart - The start point as reported by ReactFlow.
 * @param reactFlowEnd - The end point as reported by ReactFlow.
 * @returns True if the ELK edge start and end match the ReactFlow edge start and end, false otherwise.
 * @package
 */
export function doesElkStartEndMatchesReactFlowStartEnd(
  elkStart: Point,
  elkEnd: Point,
  reactFlowStart: Point,
  reactFlowEnd: Point
): boolean {
  const pointsMatch = (p1: Point, p2: Point): boolean =>
    Math.abs(p1.x - p2.x) < DRAG_BUFFER_PIXELS && Math.abs(p1.y - p2.y) < DRAG_BUFFER_PIXELS;

  return pointsMatch(elkStart, reactFlowStart) && pointsMatch(elkEnd, reactFlowEnd);
}

/**
 * Use the control points for an ELK edge to determine a position for the edge's label.
 *
 * This is a naive guess at a good label position made by taking the middle control point as the
 * position. We use this approximation when a label has been added to an edge but its label layout
 * hasn't been computed yet.
 *
 * @param points Array of control points for the edge.
 * @returns The point for label placement.
 * @package
 */
export function getFallbackLabelPosition(points: Array<Point>): {labelX?: number; labelY?: number} {
  // For simplicity, use the middle point or interpolate between middle two points
  const midIndex = Math.floor(points.length / 2);

  if (points.length % 2 === 1) {
    return {labelX: points[midIndex].x, labelY: points[midIndex].y};
  } else {
    // Interpolate between two middle points
    const p1 = points[midIndex - 1];
    const p2 = points[midIndex];
    const midPointValue = midPoint(p1, p2);
    return {labelX: midPointValue.x, labelY: midPointValue.y};
  }
}

/**
 * Get the position of an edge's label from the result ELK returned.
 *
 * @package
 */
export function getElkLabelPosition(
  label: string | React.ReactNode,
  elkEdgeLabels: Array<ElkLabel>
): {labelX?: number; labelY?: number} {
  if (label == null) {
    assert(
      elkEdgeLabels == null || elkEdgeLabels.length === 0,
      'Received ELK edge label data when label is null'
    );
    return {labelX: undefined, labelY: undefined};
  }

  assert(elkEdgeLabels.length > 0, 'No layout data received for edge label when expected.');
  return {labelX: elkEdgeLabels[0].x, labelY: elkEdgeLabels[0].y};
}

/**
 * Gets a position to use for an edge label, either from ELK layout data or using a fallback position.
 *
 * @package
 */
export function getLabelPosition(
  label: string | React.ReactNode,
  edgeLabels: Array<ElkLabel>,
  elkEdgeSections: Array<ElkEdgeSection>
): {labelX?: number; labelY?: number} {
  return edgeLabels != null && edgeLabels.length > 0
    ? getElkLabelPosition(label, edgeLabels)
    : getFallbackLabelPosition(
        elkEdgeSections
          .map(({startPoint, endPoint, bendPoints}) => [startPoint, ...(bendPoints ?? []), endPoint])
          .flat()
          .map(({x, y}) => ({x, y}))
      );
}

/**
 * Renders a SPLINE-type graph edge produced by ELK's layout system.
 *
 * The `data` property should contain the control points produced by ELK's layout system.
 */
export default function ElkSplineEdgeRenderer({
  data = DEFAULT_ELK_SPLINE_EDGE_OPTIONS,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  selected,
  label,
}: ElkSplineEdgeProps): JSX.Element | null {
  const theme = useMantineTheme();
  if (
    data.elkEdgeData == null ||
    data.elkEdgeData.sections.length === 0 ||
    !doesElkStartEndMatchesReactFlowStartEnd(
      data.elkEdgeData.sections[0].startPoint,
      data.elkEdgeData.sections[data.elkEdgeData.sections.length - 1].endPoint,
      {x: sourceX, y: sourceY},
      {x: targetX, y: targetY}
    )
  ) {
    // We fallback to the default bezier path in two cases:
    // 1. If we didn't receive any edge data from ELK.
    // 2. If there are no control points to the ELK edge except the start and end points (meaning ELK would draw a straight line).
    // 3. If ELK's edge start and end don't match ReactFlow's. In this case, the user is probably dragging the
    // nodes connected by the edge. If we kept the ELK edge in this case, the spline would stretch at the ends
    // while the rest of the edge wouldn't move, giving the user less control over how they visualize the edge
    // in that moment.
    const [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });
    return (
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={getEdgePathStyle(theme, data, selected ?? false)}
        label={label}
        labelX={labelX}
        labelY={labelY}
      />
    );
  }

  const {sections: elkEdgeSections, labels: edgeLabels} = data.elkEdgeData;
  const edgePath = elkEdgeSections
    .map(({startPoint, endPoint, bendPoints}, index) =>
      getBezierSplinePathFromElkPoints([
        index === 0 ? {x: sourceX, y: sourceY} : startPoint,
        ...(bendPoints ?? []),
        index === elkEdgeSections.length - 1 ? {x: targetX, y: targetY} : endPoint,
      ])
    )
    .join(' ');

  const {labelX, labelY} = getLabelPosition(label, edgeLabels, elkEdgeSections);

  return (
    <BaseEdge
      path={edgePath}
      markerEnd={markerEnd}
      style={getEdgePathStyle(theme, data, selected ?? false)}
      label={label}
      labelX={labelX}
      labelY={labelY}
    />
  );
}
